/** Verified representation bytes only. GPU objects and gameplay state never enter this store. */
export interface AssetDescriptor { readonly path: string; readonly url: string; readonly sha256: string; readonly bytes: number; readonly mime: string }
export interface AssetInventory { readonly schemaVersion: 1; readonly sourceSha: string; readonly manifestVersion: number; readonly assetFormatVersion: 1; readonly entries: readonly AssetDescriptor[] }
export interface CachedAsset { key: string; schemaVersion: 1; assetFormatVersion: 1; sha256: string; bytes: number; mime: string; blob: Blob; lastUsed: number }
export const MAX_CACHE_ENTRIES = 4096;
export interface AdmissionPolicy { budgetBytes: number; maxEntries: number; maxEvictions: number; protectedKeys: ReadonlySet<string> }
interface Protection { ready: Promise<void>; release(): void }
export interface AssetCacheStore {
  get(key: string): Promise<CachedAsset | undefined>;
  put(entry: CachedAsset, policy: AdmissionPolicy): Promise<boolean>;
  delete(key: string): Promise<void>;
  touch(key: string, lastUsed: number): Promise<void>;
  protect?(keys: readonly string[]): Protection;
}
export interface AssetSession { readonly inventory: AssetInventory; read(path: string, options?: { signal?: AbortSignal }): Promise<Blob>; release(): void }
const rawKey = (sha: string) => `raw-v1:${sha}`;
const references = new Map<string, number>();
const mimeTypes: Record<string, string> = { js: 'application/javascript', mjs: 'application/javascript', css: 'text/css', html: 'text/html', json: 'application/json', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', svg: 'image/svg+xml', webp: 'image/webp', woff: 'font/woff', woff2: 'font/woff2', ico: 'image/x-icon', txt: 'text/plain', map: 'application/json' };
function record(value: unknown): value is Record<string, unknown> { return !!value && typeof value === 'object' && !Array.isArray(value); }
function closed(value: Record<string, unknown>, keys: string[]): boolean { return Object.keys(value).length === keys.length && keys.every(key => Object.prototype.hasOwnProperty.call(value, key)); }
/** Matches AUTO-268 public inventory schema1; this is not a second release manifest. */
export function validateAssetInventory(value: unknown): AssetInventory {
  if (!record(value) || !closed(value, ['schemaVersion', 'sourceSha', 'manifestVersion', 'assetFormatVersion', 'entries']) || value.schemaVersion !== 1 || value.assetFormatVersion !== 1 || typeof value.sourceSha !== 'string' || !/^[a-f0-9]{40}$/.test(value.sourceSha) || !Number.isSafeInteger(value.manifestVersion) || (value.manifestVersion as number) < 1 || !Array.isArray(value.entries) || value.entries.length < 1 || value.entries.length > 20000) throw Error('Incompatible asset inventory');
  let previous = '';
  const folded = new Set<string>();
  const entries = value.entries.map((entry: unknown): AssetDescriptor => {
    if (!record(entry) || !closed(entry, ['path', 'url', 'sha256', 'bytes', 'mime'])) throw Error('Invalid asset descriptor');
    const { path, url, sha256, bytes, mime } = entry;
    if (typeof path !== 'string' || path.length > 512 || !/^[A-Za-z0-9_./-]+$/.test(path) || path.split('/').some(part => !part || part.startsWith('.')) || !['frontend', 'admin', 'game'].includes(path.split('/')[0]!) || path.endsWith('.sql') || path <= previous || folded.has(path.toLowerCase()) || url !== `/releases/${value.sourceSha}/${path}` || typeof sha256 !== 'string' || !/^[a-f0-9]{64}$/.test(sha256) || !Number.isSafeInteger(bytes) || (bytes as number) < 0 || mime !== (mimeTypes[path.substring(path.lastIndexOf('.') + 1).toLowerCase()] ?? 'application/octet-stream')) throw Error('Invalid or unpinned asset descriptor');
    previous = path; folded.add(path.toLowerCase());
    return Object.freeze({ path, url: url as string, sha256, bytes: bytes as number, mime: mime as string });
  });
  if (!['frontend/index.html', 'admin/index.html', 'game/manifest.json'].every(path => entries.some(entry => entry.path === path))) throw Error('Incomplete asset inventory');
  return Object.freeze({ schemaVersion: 1, sourceSha: value.sourceSha, manifestVersion: value.manifestVersion as number, assetFormatVersion: 1, entries: Object.freeze(entries) });
}
function cancelled(): Error { return new DOMException('Asset request cancelled', 'AbortError'); }
function check(signal: AbortSignal): void { if (signal.aborted) throw cancelled(); }
async function digest(blob: Blob): Promise<string> {
  return [...new Uint8Array(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer()))].map(byte => byte.toString(16).padStart(2, '0')).join('');
}
async function valid(blob: unknown, descriptor: AssetDescriptor): Promise<boolean> {
  return blob instanceof Blob && blob.size === descriptor.bytes && await digest(blob) === descriptor.sha256;
}
interface Download { controller: AbortController; promise: Promise<Blob>; readers: number }
// Shared within the page across game/admin/snapshot consumers and cache instances.
const downloads = new Map<string, Download>();
const defaultStore = () => createIndexedDBStore();
export class AssetCache {
  private readonly store: AssetCacheStore;
  private readonly fetcher: typeof fetch;
  private readonly budgetBytes: number;
  private readonly maxEvictions: number;
  private readonly maxEntries: number;
  private readonly counters = { networkRequests: 0, networkBytes: 0, hits: 0, hitBytes: 0 };
  constructor(options: { store?: AssetCacheStore; fetch?: typeof fetch; budgetBytes?: number; maxEntries?: number; maxEvictionsPerPass?: number } = {}) {
    this.store = options.store ?? defaultStore(); this.fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.budgetBytes = Math.max(0, Math.floor(options.budgetBytes ?? 64 * 1024 * 1024));
    this.maxEntries = Math.min(MAX_CACHE_ENTRIES, Math.max(1, Math.floor(options.maxEntries ?? MAX_CACHE_ENTRIES)));
    this.maxEvictions = Math.min(32, Math.max(0, Math.floor(options.maxEvictionsPerPass ?? 32)));
  }
  get stats() { return { ...this.counters }; }
  pinInventory(value: unknown): AssetSession {
    const inventory = validateAssetInventory(value), entries = new Map(inventory.entries.map(entry => [entry.path, entry]));
    const keys = [...new Set(inventory.entries.map(entry => rawKey(entry.sha256)))];
    for (const key of keys) references.set(key, (references.get(key) ?? 0) + 1);
    const protection = this.store.protect?.(keys);
    const lifetime = new AbortController();
    return {
      inventory,
      read: async (path, { signal } = {}) => {
        if (lifetime.signal.aborted || signal?.aborted) throw cancelled();
        const descriptor = entries.get(path); if (!descriptor) throw Error(`Asset absent from pinned inventory: ${path}`);
        const controller = new AbortController(), abort = () => controller.abort();
        lifetime.signal.addEventListener('abort', abort, { once: true }); signal?.addEventListener('abort', abort, { once: true });
        try {
          await protection?.ready; check(controller.signal);
          return await this.read(descriptor, controller.signal);
        } finally { lifetime.signal.removeEventListener('abort', abort); signal?.removeEventListener('abort', abort); }
      },
      release: () => {
        if (lifetime.signal.aborted) return;
        lifetime.abort(); protection?.release();
        for (const key of keys) { const count = references.get(key)! - 1; if (count) references.set(key, count); else references.delete(key); }
      },
    };
  }
  private async read(descriptor: AssetDescriptor, signal: AbortSignal): Promise<Blob> {
    const key = rawKey(descriptor.sha256);
    let operation = downloads.get(key);
    if (operation?.controller.signal.aborted) operation = undefined;
    if (!operation) {
      const controller = new AbortController();
      operation = { controller, readers: 0, promise: this.load(descriptor, controller.signal) };
      const created = operation; downloads.set(key, created);
      void created.promise.finally(() => { if (downloads.get(key) === created) downloads.delete(key); }).catch(() => undefined);
    }
    operation.readers++;
    const current = operation;
    try {
      const blob = await new Promise<Blob>((resolve, reject) => {
        const abort = () => reject(cancelled());
        signal.addEventListener('abort', abort, { once: true });
        current.promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
        if (signal.aborted) abort();
      });
      check(signal);
      if (blob.size !== descriptor.bytes) throw Error('Shared asset length disagrees with pinned descriptor');
      return blob.slice(0, blob.size, descriptor.mime);
    } finally { if (--current.readers === 0) current.controller.abort(); }
  }
  private async load(descriptor: AssetDescriptor, signal: AbortSignal): Promise<Blob> {
    const key = rawKey(descriptor.sha256);
    let cached: CachedAsset | undefined;
    try { cached = await this.store.get(key); } catch { /* Storage is optional. */ }
    check(signal);
    if (cached) {
      if (cached.key === key && cached.schemaVersion === 1 && cached.assetFormatVersion === 1 && cached.sha256 === descriptor.sha256 && cached.bytes === descriptor.bytes && await valid(cached.blob, descriptor)) {
        check(signal); this.counters.hits++; this.counters.hitBytes += descriptor.bytes;
        try { await this.store.touch(key, Date.now()); } catch { /* Valid bytes remain usable. */ }
        return cached.blob;
      }
      try { await this.store.delete(key); } catch { /* Network still repairs the read. */ }
    }
    let lastError: unknown;
    for (let attempt = 0; attempt < 2; attempt++) {
      check(signal);
      try {
        this.counters.networkRequests++;
        const response = await this.fetcher(descriptor.url, { signal, cache: 'no-store', credentials: 'omit' });
        if (!response.ok) throw Error(`Pinned asset unavailable (${response.status}); reload a compatible generation`);
        const blob = await response.blob(); this.counters.networkBytes += blob.size;
        if (!await valid(blob, descriptor)) throw Error('Asset representation digest or length mismatch');
        check(signal);
        if (blob.size <= this.budgetBytes) {
          try { await this.store.put({ key, schemaVersion: 1, assetFormatVersion: 1, sha256: descriptor.sha256, bytes: blob.size, mime: descriptor.mime, blob, lastUsed: Date.now() }, { budgetBytes: this.budgetBytes, maxEntries: this.maxEntries, maxEvictions: this.maxEvictions, protectedKeys: new Set(references.keys()) }); } catch { /* Denial/quota/write errors do not affect gameplay. */ }
        }
        check(signal); return blob;
      } catch (error) { check(signal); lastError = error; }
    }
    throw lastError;
  }
}

/** One atomic asset+metadata publication. Metadata scans never clone stored Blob bodies. */
export function createIndexedDBStore({ name = 'autohero-raw-assets', timeoutMs = 1000 }: { name?: string; timeoutMs?: number } = {}): AssetCacheStore {
  let opening: Promise<IDBDatabase | undefined> | undefined, disabled = false;
  const lockName = (key: string) => `${name}:${key}`;
  const lockManager = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  function open(): Promise<IDBDatabase | undefined> {
    if (disabled) return Promise.resolve(undefined);
    return opening ??= new Promise(resolve => {
      let settled = false;
      const finish = (db?: IDBDatabase) => { if (settled) { db?.close(); return; } settled = true; clearTimeout(timer); resolve(db); };
      const timer = setTimeout(() => { disabled = true; finish(); }, timeoutMs);
      try {
        const request = indexedDB.open(name, 1);
        request.onblocked = request.onerror = () => { disabled = true; finish(); };
        request.onupgradeneeded = () => {
          if (settled) { request.transaction?.abort(); return; }
          request.result.createObjectStore('assets', { keyPath: 'key' });
          request.result.createObjectStore('metadata', { keyPath: 'key' });
        };
        request.onsuccess = () => {
          const db = request.result;
          db.onversionchange = () => { disabled = true; db.close(); };
          finish(db);
        };
      } catch { disabled = true; finish(); }
    });
  }
  async function transact<T>(mode: IDBTransactionMode, action: (transaction: IDBTransaction, set: (value: T) => void) => void): Promise<T | undefined> {
    const db = await open(); if (!db) return undefined;
    return new Promise((resolve, reject) => {
      let transaction: IDBTransaction, value: T | undefined;
      const timer = setTimeout(() => { disabled = true; transaction?.abort(); db.close(); reject(Error('Asset storage timed out')); }, timeoutMs);
      try {
        transaction = db.transaction(['assets', 'metadata'], mode);
        transaction.oncomplete = () => { clearTimeout(timer); resolve(value); };
        transaction.onabort = transaction.onerror = () => { clearTimeout(timer); reject(transaction.error ?? Error('Asset storage failed')); };
        action(transaction, result => { value = result; });
      } catch (error) { clearTimeout(timer); reject(error); }
    });
  }
  interface Metadata { key: string; bytes: number; lastUsed: number }
  function validMetadata(rows: Metadata[]): boolean {
    return rows.length <= MAX_CACHE_ENTRIES && rows.every(row =>
      record(row) && typeof row.key === 'string' && /^raw-v1:[a-f0-9]{64}$/.test(row.key) &&
      Number.isSafeInteger(row.bytes) && row.bytes >= 0 &&
      Number.isSafeInteger(row.lastUsed) && row.lastUsed >= 0) &&
      Number.isSafeInteger(rows.reduce((sum, row) => sum + row.bytes, 0));
  }
  return {
    async get(key) { return transact<CachedAsset>('readonly', (transaction, set) => { const request = transaction.objectStore('assets').get(key); request.onsuccess = () => set(request.result as CachedAsset); }); },
    async delete(key) { await transact('readwrite', transaction => { transaction.objectStore('assets').delete(key); transaction.objectStore('metadata').delete(key); }); },
    async touch(key, lastUsed) { await transact('readwrite', transaction => {
      for (const storeName of ['assets', 'metadata']) { const store = transaction.objectStore(storeName), request = store.get(key); request.onsuccess = () => { if (request.result) store.put({ ...request.result, lastUsed }); }; }
    }); },
    protect(keys) {
      if (!lockManager) return { ready: Promise.resolve(), release() {} };
      let released = false, release!: () => void;
      const controller = new AbortController();
      const lifetime = new Promise<void>(resolve => { release = resolve; });
      const pending = Promise.all(keys.map(key => new Promise<void>(resolve => {
        void lockManager.request(lockName(key), { mode: 'shared', signal: controller.signal }, async () => { resolve(); if (!released) await lifetime; }).catch(() => resolve());
      })));
      let timer: ReturnType<typeof setTimeout>;
      // Another context must never stall startup by holding an exclusive lock.
      const timeout = new Promise<void>(resolve => { timer = setTimeout(() => { disabled = true; controller.abort(); released = true; release(); resolve(); }, timeoutMs); });
      const ready = Promise.race([pending, timeout]).then(() => { clearTimeout(timer); });
      return { ready, release() { released = true; controller.abort(); release(); } };
    },
    async put(entry, policy) {
      if (!validMetadata([entry]) || !Number.isSafeInteger(entry.bytes) || entry.bytes < 0 ||
          !Number.isSafeInteger(policy.budgetBytes) || policy.budgetBytes < 0 || entry.bytes > policy.budgetBytes ||
          !Number.isSafeInteger(policy.maxEntries) || policy.maxEntries < 1 || policy.maxEntries > MAX_CACHE_ENTRIES ||
          !Number.isSafeInteger(policy.maxEvictions) || policy.maxEvictions < 0 || policy.maxEvictions > 32) return false;
      // Lock acquisition is outside the transaction; no async gap inside IDB writes.
      const snapshot = await transact<Metadata[]>('readonly', (transaction, set) => { const request = transaction.objectStore('metadata').getAll(undefined, MAX_CACHE_ENTRIES + 1); request.onsuccess = () => set(request.result as Metadata[]); });
      if (!snapshot || !validMetadata(snapshot)) return false;
      const candidates = snapshot.filter(row => row.key !== entry.key && !policy.protectedKeys.has(row.key)).sort((a, b) => a.lastUsed - b.lastUsed).slice(0, policy.maxEvictions ? 32 : 0);
      const exclusive = new Set<string>(); let release!: () => void;
      const lifetime = new Promise<void>(resolve => { release = resolve; });
      if (lockManager) await Promise.all(candidates.map(row => new Promise<void>(resolve => {
        void lockManager.request(lockName(row.key), { mode: 'exclusive', ifAvailable: true }, async lock => { if (lock) exclusive.add(row.key); resolve(); if (lock) await lifetime; }).catch(() => resolve());
      })));
      try {
        return await transact<boolean>('readwrite', (transaction, set) => {
          const metadata = transaction.objectStore('metadata'), assets = transaction.objectStore('assets'), request = metadata.getAll(undefined, MAX_CACHE_ENTRIES + 1);
          request.onsuccess = () => {
            const rows = request.result as Metadata[];
            if (!validMetadata(rows)) { set(false); return; }
            let count = rows.filter(row => row.key !== entry.key).length;
            let total = rows.filter(row => row.key !== entry.key).reduce((sum, row) => sum + row.bytes, 0), removed = 0;
            for (const row of rows.sort((a, b) => a.lastUsed - b.lastUsed)) {
              if ((total + entry.bytes <= policy.budgetBytes && count + 1 <= policy.maxEntries) || removed >= policy.maxEvictions) break;
              if (row.key === entry.key || policy.protectedKeys.has(row.key) || !exclusive.has(row.key)) continue;
              assets.delete(row.key); metadata.delete(row.key); total -= row.bytes; count--; removed++;
            }
            if (total + entry.bytes > policy.budgetBytes || count + 1 > policy.maxEntries) { set(false); return; }
            const { key, bytes, lastUsed } = entry;
            assets.put(entry); metadata.put({ key, bytes, lastUsed }); set(true);
          };
        }) ?? false;
      } finally { release(); }
    },
  };
}
